/** What each session's send or turn start is doing, for views not on its
 *  turn stream.
 *
 * Fed by the session bus's ``session.progress`` events
 * (``useSessionEventStream``): a send's phases before it answers — the
 * message on its way shows them — and a turn's, mirrored for the other
 * viewers until they are on its stream.
 *
 * Kept per server reporter (``origin``), the newest shown: two sends can
 * race until one wins the turn-start lock, and the loser's clear must
 * leave the winner's phase standing. A turn's phase replaces every other
 * (its send's included, for good); a clear naming no origin drops all.
 *
 * The bus has no replay, so an entry must never outlive a lost clear:
 * the stream's reconnect forgets them all, a ``session.updated`` (the
 * turn's end publishes one) forgets a turn's, and a new send starts from
 * nothing.
 */
import { useCallback, useSyncExternalStore } from "react";

import {
  parseProgress,
  stampProgress,
  type ProgressPhase,
  type ProgressScope,
} from "@/features/sessions/progress";

// Per session: one phase per origin, oldest first; the last is shown.
const store = new Map<string, ProgressPhase[]>();
const listeners = new Set<() => void>();

function changed(): void {
  for (const listener of listeners) listener();
}

/** Record a ``session.progress`` payload for ``sessionId``. ``newTurn``
 *  is true for the first phase of a turn not seen before, so the caller
 *  can refetch the session and join the turn's stream. */
export function recordSessionProgress(
  sessionId: string,
  raw: unknown,
  now: number = Date.now(),
): { newTurn: boolean } {
  const wire = sessionId ? parseProgress(raw) : null;
  if (!wire) return { newTurn: false };
  const list = store.get(sessionId) ?? [];
  const shown = list.at(-1) ?? null;
  if (wire.phase === null) {
    const rest = wire.origin === null ? [] : list.filter((e) => e.origin !== wire.origin);
    update(sessionId, list, rest);
    return { newTurn: false };
  }
  const own = list.find((e) => e.origin === wire.origin) ?? null;
  const phase = stampProgress({ ...wire, phase: wire.phase }, own, now);
  update(
    sessionId,
    list,
    phase.scope === "turn" ? [phase] : [...list.filter((e) => e.origin !== wire.origin), phase],
  );
  return {
    newTurn: phase.scope === "turn" && phase.turnId !== null && shown?.turnId !== phase.turnId,
  };
}

function update(sessionId: string, before: ProgressPhase[], after: ProgressPhase[]): void {
  if (after.length === before.length && after.every((e, i) => e === before[i])) return;
  if (after.length === 0) store.delete(sessionId);
  else store.set(sessionId, after);
  changed();
}

/** Forget the session's phases — of one ``scope``, or all of them. */
export function clearSessionProgress(sessionId: string, scope?: ProgressScope): void {
  const list = store.get(sessionId);
  if (!list) return;
  update(sessionId, list, scope ? list.filter((e) => e.scope !== scope) : []);
}

/** Forget every session's phase (a reconnect may have missed clears). */
export function resetSessionProgress(): void {
  if (store.size === 0) return;
  store.clear();
  changed();
}

/** The session's current phase, as render state. */
export function useSessionProgress(sessionId: string | undefined): ProgressPhase | null {
  const subscribe = useCallback((listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => (sessionId ? store.get(sessionId)?.at(-1) ?? null : null),
    () => null,
  );
}
